import { forwardRef } from 'react';
import type { CSSProperties } from 'react';
import { FiCheck } from 'react-icons/fi';
import type { BadgeProps } from '@highlight-cp/core-ui';
import { Badge, Box } from '@highlight-cp/core-ui';

import classes from './status-badge.module.css';
import { getStatusBadgeColor, getStatusBadgeLabel, isDoneStatus } from './utils';

/** `color`, `c` and `bg` come from the status, so they aren't the caller's to set. */
export interface StatusBadgeProps extends Omit<BadgeProps, 'children' | 'color' | 'c' | 'bg'> {
  status?: string | null;
}

/**
 * A Badge led by a dot, both coloured from a Highlight project/survey/response status. The label
 * itself stays neutral — the hue-tinted text Mantine's `light` variant would use reads far heavier
 * than the dot needs it to. A finished status leads with a check on a neutral pill instead, so it
 * doesn't read as another hue in the workflow. Renders nothing when the status is empty. Badge props
 * (and the ref) pass through so the badge can act as a Tooltip/Popover target; pass a `leftSection`
 * to render something other than the dot.
 */
export const StatusBadge = forwardRef<HTMLDivElement, StatusBadgeProps>(
  ({ status, leftSection, ...badgeProps }, ref) => {
    if (!status) {
      return null;
    }

    const color = getStatusBadgeColor(status);

    const marker = isDoneStatus(status) ? (
      // Decorative: the label beside it already says the work is done.
      <FiCheck aria-hidden className={classes.check} />
    ) : (
      <Box
        className={classes.dot}
        style={
          {
            '--status-dot-color': `var(--mantine-color-${color}-filled)`,
          } as CSSProperties
        }
      />
    );

    return (
      <Badge
        {...badgeProps}
        ref={ref}
        color={color}
        c="dimmed"
        // A step darker than the `light` variant's own tint, which reads washed out at this size.
        bg={`var(--mantine-color-${color}-light-hover)`}
        leftSection={leftSection ?? marker}
      >
        {getStatusBadgeLabel(status)}
      </Badge>
    );
  },
);

StatusBadge.displayName = 'StatusBadge';
